import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
type CanvasCollapse = {
  /** False while suggestions are pending — cards force-expand so nothing hides. */
  collapsible: boolean;
  isExpanded: (id: string) => boolean;
  toggle: (id: string) => void;
  allExpanded: boolean;
  toggleAll: () => void;
};
const CanvasCollapseContext = createContext<CanvasCollapse | null>(null);
type ProviderProps = {
  /** All collapsible node ids, in order — drives expand/collapse-all. */
  nodeIds: string[];
  /** Nodes that must stay open (validation errors, the just-added node) — expanded additively. */
  keepExpanded: string[];
  /** Nodes that start expanded (input schema, rules) but the user can still collapse. */
  defaultExpanded: string[];
  /** When set, every card renders expanded and chevrons hide (e.g. a Percy turn left pending changes). */
  forceExpanded: boolean;
  children: ReactNode;
};
export const CanvasCollapseProvider = t0 => {
  const $ = _c(34);
  const {
    nodeIds,
    keepExpanded,
    defaultExpanded,
    forceExpanded,
    children
  } = t0;
  let t1;
  if ($[0] !== defaultExpanded) {
    t1 = () => new Set(defaultExpanded);
    $[0] = defaultExpanded;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [expanded, setExpanded] = useState(t1);
  let t2;
  if ($[2] !== defaultExpanded) {
    t2 = new Set(defaultExpanded);
    $[2] = defaultExpanded;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const seededRef = useRef(t2);
  let t3;
  if ($[4] !== defaultExpanded) {
    t3 = defaultExpanded.join("\n");
    $[4] = defaultExpanded;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const defaultKey = t3;
  let t4;
  if ($[6] !== defaultExpanded) {
    t4 = () => {
      const fresh = defaultExpanded.filter(id => !seededRef.current.has(id));
      if (fresh.length === 0) {
        return;
      }
      for (const id_0 of fresh) {
        seededRef.current.add(id_0);
      }
      setExpanded(prev => new Set([...prev, ...fresh]));
    };
    $[6] = defaultExpanded;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== defaultKey) {
    t5 = [defaultKey];
    $[8] = defaultKey;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== keepExpanded) {
    t6 = keepExpanded.join("\n");
    $[10] = keepExpanded;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const keepKey = t6;
  let t7;
  if ($[12] !== keepExpanded) {
    t7 = () => {
      if (keepExpanded.length === 0) {
        return;
      }
      setExpanded(prev_0 => {
        if (keepExpanded.every(id_1 => prev_0.has(id_1))) {
          return prev_0;
        }
        return new Set([...prev_0, ...keepExpanded]);
      });
    };
    $[12] = keepExpanded;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== keepKey) {
    t8 = [keepKey];
    $[14] = keepKey;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  useEffect(t7, t8);
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = id_2 => {
      setExpanded(prev_1 => {
        const next = new Set(prev_1);
        if (next.has(id_2)) {
          next.delete(id_2);
        } else {
          next.add(id_2);
        }
        return next;
      });
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const toggle = t9;
  let t10;
  if ($[17] !== expanded || $[18] !== nodeIds) {
    t10 = nodeIds.length > 0 && nodeIds.every(id_3 => expanded.has(id_3));
    $[17] = expanded;
    $[18] = nodeIds;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const allExpanded = t10;
  let t11;
  if ($[20] !== allExpanded || $[21] !== nodeIds) {
    t11 = () => setExpanded(allExpanded ? new Set() : new Set(nodeIds));
    $[20] = allExpanded;
    $[21] = nodeIds;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const toggleAll = t11;
  const t12 = !forceExpanded;
  let t13;
  if ($[23] !== expanded || $[24] !== forceExpanded) {
    t13 = id_4 => forceExpanded || expanded.has(id_4);
    $[23] = expanded;
    $[24] = forceExpanded;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== allExpanded || $[27] !== t12 || $[28] !== t13 || $[29] !== toggleAll) {
    t14 = {
      collapsible: t12,
      isExpanded: t13,
      toggle,
      allExpanded,
      toggleAll
    };
    $[26] = allExpanded;
    $[27] = t12;
    $[28] = t13;
    $[29] = toggleAll;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const value = t14;
  let t15;
  if ($[31] !== children || $[32] !== value) {
    t15 = <CanvasCollapseContext.Provider value={value}>{children}</CanvasCollapseContext.Provider>;
    $[31] = children;
    $[32] = value;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
};

/** Null outside a provider — blocks rendered elsewhere stay fully expanded. */
export const useCanvasCollapse = () => {
  return useContext(CanvasCollapseContext);
};
type NodeCollapse = {
  collapsible: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  collapseAriaLabel: string;
};

/** Per-card collapse wiring — returns the collapse-related `PlanBlock` props for a node. */
export const useNodeCollapse = (id, expandLabel, collapseLabel) => {
  const $ = _c(12);
  const collapse = useCanvasCollapse();
  const collapsible = collapse?.collapsible ?? false;
  let t0;
  if ($[0] !== collapse || $[1] !== collapsible || $[2] !== id) {
    t0 = collapsible && !collapse?.isExpanded(id);
    $[0] = collapse;
    $[1] = collapsible;
    $[2] = id;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const collapsed = t0;
  let t1;
  if ($[4] !== collapse || $[5] !== id) {
    t1 = () => collapse?.toggle(id);
    $[4] = collapse;
    $[5] = id;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = collapsed ? expandLabel : collapseLabel;
  let t3;
  if ($[7] !== collapsed || $[8] !== collapsible || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      collapsible,
      collapsed,
      onToggleCollapsed: t1,
      collapseAriaLabel: t2
    };
    $[7] = collapsed;
    $[8] = collapsible;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};